import { useEffect, useState } from 'react';
import {
  CatalogProduct,
  formatMoney,
  ensureCatalog,
  getCatalogProduct,
  getVariantById,
} from './catalog';
import {
  ComboQuoteLine,
  fetchComboData,
  resolveComboQuoteLines,
} from './combo';
import { LiveProduct, comboDiscountId } from './utils';

export interface AppliedQuoteDiscount {
  discount_id: string;
  discount_title: string;
  discount_type: string;
  total_discount: number;
  live_exclusive?: boolean;
}

export interface ProductQuote {
  catalogPrice: number | null;
  compareAt: number | null;
  originalTotal: number;
  voucherDiscount: number;
  liveVoucherDiscount: number;
  preLiveTotal: number;
  totalDiscount: number;
  finalTotal: number;
  freeShipping: boolean;
  voucherLabel: string | null;
  appliedTitles: string[];
  /** Discount UUIDs applied for this single-item quote (incl. free shipping when eligible) */
  appliedDiscountIds: string[];
  /** Full applied discount rows for voucher rail highlight / inclusion */
  appliedDiscounts: AppliedQuoteDiscount[];
}

export interface ComboSelectedLine {
  variantId: string;
  quantity: number;
}

const quoteCache = new Map<string, { at: number; quote: ProductQuote }>();
const QUOTE_TTL_MS = 20_000;
let quoteCacheGeneration = 0;
const quoteCacheListeners = new Set<() => void>();

export function clearQuoteCache(): void {
  quoteCache.clear();
  quoteCacheGeneration += 1;
  quoteCacheListeners.forEach((listener) => listener());
}
const COMBO_PRICE_EPS = 0.05;

type CalcApplied = {
  discount_id?: string;
  discount_type: string;
  discount_title: string;
  total_discount: number;
  live_exclusive?: boolean;
};

function emptyQuote(
  catalogPrice: number | null,
  compareAt: number | null
): ProductQuote {
  const finalTotal = catalogPrice || 0;
  return {
    catalogPrice,
    compareAt,
    originalTotal: catalogPrice || 0,
    voucherDiscount: 0,
    liveVoucherDiscount: 0,
    preLiveTotal: finalTotal,
    totalDiscount: 0,
    finalTotal,
    freeShipping: false,
    voucherLabel: null,
    appliedTitles: [],
    appliedDiscountIds: [],
    appliedDiscounts: [],
  };
}

function quoteFromCalculation(
  calc: {
    applied_discounts?: CalcApplied[];
    shipping_discount?: {
      eligible?: boolean;
      discount_id?: string;
      discount_title?: string;
    };
    total_discount?: number;
    original_total?: number;
    final_total?: number;
  },
  catalogPrice: number,
  compareAt: number | null
): ProductQuote {
  const applied = (calc.applied_discounts || []) as CalcApplied[];
  const voucherDiscount = applied
    .filter((d) => d.discount_type === 'voucher')
    .reduce((s, d) => s + Number(d.total_discount || 0), 0);
  const liveVoucherDiscount = applied
    .filter((d) => d.discount_type === 'voucher' && d.live_exclusive)
    .reduce((s, d) => s + Number(d.total_discount || 0), 0);
  const voucherTitles = applied
    .filter((d) => d.discount_type === 'voucher')
    .map((d) => d.discount_title)
    .filter(Boolean);
  const appliedDiscounts: AppliedQuoteDiscount[] = applied
    .filter((d) => Boolean(d.discount_id))
    .map((d) => ({
      discount_id: String(d.discount_id),
      discount_title: d.discount_title || 'LIVE deal',
      discount_type: d.discount_type || 'voucher',
      total_discount: Number(d.total_discount || 0),
      live_exclusive: Boolean(d.live_exclusive),
    }));
  if (
    calc.shipping_discount?.eligible &&
    calc.shipping_discount?.discount_id &&
    !appliedDiscounts.some(
      (d) => d.discount_id === String(calc.shipping_discount!.discount_id)
    )
  ) {
    appliedDiscounts.push({
      discount_id: String(calc.shipping_discount.discount_id),
      discount_title: calc.shipping_discount.discount_title || 'Free shipping',
      discount_type: 'free_shipping',
      total_discount: 0,
    });
  }
  const totalDiscount = Number(calc.total_discount || 0);
  const originalTotal = Number(
    calc.original_total != null ? calc.original_total : catalogPrice
  );
  const finalTotal = Number(
    calc.final_total != null ? calc.final_total : originalTotal - totalDiscount
  );
  return {
    catalogPrice,
    compareAt,
    originalTotal,
    voucherDiscount,
    liveVoucherDiscount,
    preLiveTotal: finalTotal + liveVoucherDiscount,
    totalDiscount,
    finalTotal,
    freeShipping: Boolean(calc.shipping_discount?.eligible),
    voucherLabel:
      voucherTitles[0] ||
      (voucherDiscount > 0 ? `-RM ${formatMoney(voucherDiscount)}` : null),
    appliedTitles: appliedDiscounts.map((d) => d.discount_title).filter(Boolean),
    appliedDiscountIds: Array.from(new Set(appliedDiscounts.map((d) => d.discount_id))),
    appliedDiscounts,
  };
}

/** Keep voucher + free-shipping only, priced off the known combo total. */
function overlayVouchersOnCombo(
  calcQuote: ProductQuote,
  comboPrice: number,
  compareAt: number | null
): ProductQuote {
  const voucherDiscount = calcQuote.voucherDiscount;
  const liveVoucherDiscount = calcQuote.liveVoucherDiscount;
  const appliedDiscounts = calcQuote.appliedDiscounts.filter(
    (d) => d.discount_type === 'voucher' || d.discount_type === 'free_shipping'
  );
  const finalTotal = Math.max(0, comboPrice - voucherDiscount);
  return {
    catalogPrice: comboPrice,
    compareAt,
    originalTotal: comboPrice,
    voucherDiscount,
    liveVoucherDiscount,
    preLiveTotal: finalTotal + liveVoucherDiscount,
    totalDiscount: voucherDiscount,
    finalTotal,
    freeShipping: calcQuote.freeShipping,
    voucherLabel: calcQuote.voucherLabel,
    appliedTitles: appliedDiscounts.map((d) => d.discount_title).filter(Boolean),
    appliedDiscountIds: Array.from(new Set(appliedDiscounts.map((d) => d.discount_id))),
    appliedDiscounts,
  };
}

async function fetchCalculate(
  apiUrl: string,
  items: Array<{
    key: string;
    product_id: string;
    variant_id: string;
    price: number;
    quantity: number;
    title: string;
  }>,
  liveSessionId?: string
): Promise<ProductQuote | null> {
  if (!items.length) return null;
  const catalogPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  try {
    const res = await fetch(`${apiUrl}/discounts/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cart: {
          items,
          ...(liveSessionId ? { live_session_id: liveSessionId } : {}),
        },
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) return emptyQuote(catalogPrice, null);
    return quoteFromCalculation(data.data || {}, catalogPrice, null);
  } catch {
    return emptyQuote(catalogPrice, null);
  }
}

function comboLinesToCartItems(lines: ComboQuoteLine[]) {
  return lines.map((line, i) => ({
    key: `live_combo_quote_${line.variant_id}_${i}`,
    product_id: String(line.product_id),
    variant_id: String(line.variant_id),
    price: line.price,
    quantity: line.quantity || 1,
    title: line.title,
  }));
}

async function quoteComboProduct(
  apiUrl: string,
  product: LiveProduct,
  liveSessionId: string | undefined,
  selectedLines?: ComboSelectedLine[] | null
): Promise<ProductQuote | null> {
  const info = getCatalogProduct(product.item_id);
  const placeholderPrice =
    info?.price != null && Number.isFinite(Number(info.price))
      ? Number(info.price)
      : null;
  const placeholderCompare =
    info?.compareAt != null && Number.isFinite(Number(info.compareAt))
      ? Number(info.compareAt)
      : null;

  const discountId = comboDiscountId(product);
  const comboData = discountId ? await fetchComboData(apiUrl, discountId) : null;
  const comboPrice =
    comboData && Number(comboData.whole_discount_value) > 0
      ? Number(comboData.whole_discount_value)
      : placeholderPrice;

  const childLines = resolveComboQuoteLines(comboData, selectedLines);
  if (childLines.length) {
    const childQuote = await fetchCalculate(
      apiUrl,
      comboLinesToCartItems(childLines),
      liveSessionId
    );
    if (childQuote) {
      const bogoApplied = childQuote.appliedDiscounts.some(
        (d) => d.discount_type === 'bogo'
      );
      const withinComboTotal =
        comboPrice == null || childQuote.finalTotal <= comboPrice + COMBO_PRICE_EPS;
      // Child-line quote matches checkout (BOGO + auto-apply voucher) when the
      // rule rematches. Otherwise don't advertise the undiscounted child sum.
      if (bogoApplied || withinComboTotal) {
        return {
          ...childQuote,
          catalogPrice: comboPrice ?? childQuote.catalogPrice,
          compareAt:
            placeholderCompare != null &&
            (comboPrice == null || placeholderCompare > comboPrice)
              ? placeholderCompare
              : childQuote.originalTotal > (comboPrice ?? childQuote.finalTotal)
                ? childQuote.originalTotal
                : placeholderCompare,
        };
      }
    }
  }

  // Fallback: overlay auto-apply vouchers on the known combo total. Ignore
  // non-voucher discounts that might match the placeholder SKU itself.
  const placeholderVariantId = info?.variantId;
  if (comboPrice == null) return null;
  if (!placeholderVariantId) return emptyQuote(comboPrice, placeholderCompare);
  const placeholderQuote = await fetchCalculate(
    apiUrl,
    [
      {
        key: `live_quote_${placeholderVariantId}`,
        product_id: String(product.item_id),
        variant_id: String(placeholderVariantId),
        price: comboPrice,
        quantity: 1,
        title: info?.title || product.item_name || product.item_id,
      },
    ],
    liveSessionId
  );
  if (!placeholderQuote) return emptyQuote(comboPrice, placeholderCompare);
  return overlayVouchersOnCombo(placeholderQuote, comboPrice, placeholderCompare);
}

export async function quoteProductCheckout(
  apiUrl: string,
  product: LiveProduct,
  variantId?: string | null,
  liveSessionId?: string,
  comboLines?: ComboSelectedLine[] | null
): Promise<ProductQuote | null> {
  await ensureCatalog();
  const info = getCatalogProduct(product.item_id);
  const variant = info
    ? getVariantById(info, variantId || info.variantId)
    : null;

  const resolvedVariantId = variant?.id || variantId || info?.variantId || null;
  const comboId = comboDiscountId(product);
  const comboLinesKey = comboLines?.length
    ? comboLines.map((l) => `${l.variantId}x${l.quantity}`).join(',')
    : 'default';
  const cacheKey = comboId
    ? `${apiUrl}:${product.item_id}:combo:${comboLinesKey}:${liveSessionId || ''}`
    : `${apiUrl}:${product.item_id}:${resolvedVariantId || 'default'}:${liveSessionId || ''}`;
  const cached = quoteCache.get(cacheKey);
  if (cached && Date.now() - cached.at < QUOTE_TTL_MS) return cached.quote;

  if (comboId) {
    const quote = await quoteComboProduct(apiUrl, product, liveSessionId, comboLines);
    if (quote) quoteCache.set(cacheKey, { at: Date.now(), quote });
    return quote;
  }

  const catalogPrice =
    variant?.price != null
      ? Number(variant.price)
      : info?.price != null
        ? Number(info.price)
        : null;
  const compareAt =
    variant?.compareAt != null
      ? Number(variant.compareAt)
      : info?.compareAt != null
        ? Number(info.compareAt)
        : null;

  if (!resolvedVariantId || catalogPrice == null) {
    return emptyQuote(catalogPrice, compareAt);
  }

  const quote = await fetchCalculate(
    apiUrl,
    [
      {
        key: `live_quote_${resolvedVariantId}`,
        product_id: String(product.item_id),
        variant_id: String(resolvedVariantId),
        price: catalogPrice,
        quantity: 1,
        title: info?.title || product.item_name || product.item_id,
      },
    ],
    liveSessionId
  );
  if (!quote) return emptyQuote(catalogPrice, compareAt);
  const withCatalog: ProductQuote = { ...quote, catalogPrice, compareAt };
  quoteCache.set(cacheKey, { at: Date.now(), quote: withCatalog });
  return withCatalog;
}

export function useProductQuote(
  apiUrl: string,
  product: LiveProduct | null | undefined,
  variantId?: string | null,
  liveSessionId?: string,
  comboLines?: ComboSelectedLine[] | null
): ProductQuote | null {
  const [quote, setQuote] = useState<ProductQuote | null>(null);
  const [cacheGeneration, setCacheGeneration] = useState(quoteCacheGeneration);
  const comboLinesKey = comboLines?.length
    ? comboLines.map((l) => `${l.variantId}x${l.quantity}`).join(',')
    : '';

  useEffect(() => {
    const onClear = () => setCacheGeneration(quoteCacheGeneration);
    quoteCacheListeners.add(onClear);
    return () => {
      quoteCacheListeners.delete(onClear);
    };
  }, []);

  useEffect(() => {
    if (!product) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    quoteProductCheckout(apiUrl, product, variantId, liveSessionId, comboLines).then(
      (q) => {
        if (!cancelled) setQuote(q);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [apiUrl, product?.item_id, variantId, liveSessionId, comboLinesKey, cacheGeneration]);

  return quote;
}

export interface LivePriceDisplay {
  now: number | null;
  /** Struck price after normal vouchers, before the live voucher. */
  preLive: number | null;
  /** Struck compare-at when it is higher than the pre-live / catalog price. */
  compare: number | null;
}

/** Double-slash layout when a live voucher reduced the price. */
export function livePriceDisplay(
  finalPrice: number | null,
  quote: ProductQuote | null,
  compareAt: number | null
): LivePriceDisplay {
  const now = finalPrice;
  const liveOff = quote && quote.liveVoucherDiscount > 0.001 ? quote.liveVoucherDiscount : 0;
  if (now != null && liveOff > 0) {
    const preLive = quote?.preLiveTotal != null ? quote.preLiveTotal : now + liveOff;
    const showPreLive = preLive > now + 0.001 ? preLive : null;
    const showCompare =
      compareAt != null && showPreLive != null && compareAt > showPreLive + 0.001
        ? compareAt
        : compareAt != null && showPreLive == null && compareAt > now + 0.001
          ? compareAt
          : null;
    return { now, preLive: showPreLive, compare: showCompare };
  }
  const showCompare =
    compareAt != null && now != null && compareAt > now + 0.001 ? compareAt : null;
  return { now, preLive: null, compare: showCompare };
}

export function displayCompare(
  quote: ProductQuote | null,
  info: CatalogProduct | null,
  variantCompareAt?: number | null
): number | null {
  if (quote?.compareAt != null && quote.catalogPrice != null && quote.compareAt > quote.catalogPrice) {
    return quote.compareAt;
  }
  if (
    variantCompareAt != null &&
    quote?.catalogPrice != null &&
    variantCompareAt > quote.catalogPrice
  ) {
    return variantCompareAt;
  }
  if (info?.compareAt != null && info.price != null && Number(info.compareAt) > Number(info.price)) {
    return Number(info.compareAt);
  }
  if (quote && quote.originalTotal > quote.finalTotal) {
    return quote.originalTotal;
  }
  return null;
}

export { formatMoney };
